import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import axios from "axios";
import "./App.css";

// Terms Modal Component
const TermsModal = ({ isOpen, onAccept, onLogout, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2>Terms of Service & Privacy Policy</h2>
        <div className="modal-body">
          <p>
            Welcome to HabiTrack! Before you can continue, you must accept our
            terms of service and privacy policy.
          </p>
          <div className="terms-content">
            <h3>Terms of Service</h3>
            <p>
              By using HabiTrack, you agree to track your habits responsibly and
              engage positively with your cohort.
            </p>

            <h3>Privacy Policy</h3>
            <p>
              We collect and use your data to provide the habit tracking service
              and facilitate cohort interactions. Your habit data and progress
              will be shared with your assigned cohort members.
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button
            className="btn btn-secondary"
            onClick={onLogout}
            disabled={loading}
          >
            Decline & Logout
          </button>
          <button
            className="btn btn-primary"
            onClick={onAccept}
            disabled={loading}
          >
            {loading ? "Accepting..." : "Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Header Component
const Header = ({ programmeName }) => {
  const { logout, user, getAccessTokenSilently } = useAuth0();

  const handleLogout = () => {
    // Logout preserves backend user state - no need to clear anything
    console.log("Debug: Logging out, backend state preserved");
    logout({ returnTo: window.location.origin });
  };

  const debugUserStatus = async () => {
    try {
      console.log("=== DEBUG: Auth0 Status ===");
      console.log("user:", user);

      const token = await getAccessTokenSilently();
      console.log("=== DEBUG: Token Info ===");
      console.log("Token length:", token ? token.length : 0);
      console.log("Token start:", token ? token.substring(0, 100) : "NO_TOKEN");

      // Decode JWT token payload for debugging
      if (token) {
        try {
          const base64Url = token.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join(""),
          );
          const payload = JSON.parse(jsonPayload);
          console.log("=== DEBUG: JWT Payload ===");
          console.log("Sub:", payload.sub);
          console.log("Email:", payload.email);
          console.log("Audience:", payload.aud, "Type:", typeof payload.aud);
          console.log("Issuer:", payload.iss);
          console.log("Expires:", new Date(payload.exp * 1000));
          console.log("Full payload:", payload);
        } catch (decodeError) {
          console.error("Error decoding JWT:", decodeError);
        }
      }

      api.defaults.headers.Authorization = `Bearer ${token}`;

      // Test simple auth endpoint first
      console.log("=== DEBUG: Testing simple auth endpoint ===");
      try {
        const simpleResponse = await api.get("/api/v1/auth/simple-test");
        console.log("Simple auth test SUCCESS:", simpleResponse.data);
      } catch (simpleError) {
        console.error(
          "Simple auth test FAILED:",
          simpleError.response?.data || simpleError.message,
        );
      }

      // Test debug endpoint
      console.log("=== DEBUG: Testing debug endpoint ===");
      try {
        const debugResponse = await api.get("/api/v1/auth/debug-token");
        console.log("Debug endpoint response:", debugResponse.data);
      } catch (debugError) {
        console.error(
          "Debug endpoint FAILED:",
          debugError.response?.data || debugError.message,
        );
      }

      // Test user flow status endpoint
      console.log("=== DEBUG: Testing user flow status endpoint ===");
      try {
        const response = await api.get("/api/v1/auth/user-flow-status");
        console.log(
          "User flow status SUCCESS:",
          JSON.stringify(response.data, null, 2),
        );
      } catch (flowError) {
        console.error(
          "User flow status FAILED:",
          flowError.response?.data || flowError.message,
        );
      }

      console.log("=== DEBUG: LocalStorage state ===");
      console.log(
        "habitrack_visited:",
        localStorage.getItem("habitrack_visited"),
      );
      console.log("habit_logs:", localStorage.getItem("habit_logs"));
      console.log(
        "user_endorsements:",
        localStorage.getItem("user_endorsements"),
      );
      console.log("=====================================");
    } catch (error) {
      console.error("Error fetching user status:", error);
      console.log("Full error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
      });
    }
  };

  const resetUserState = async () => {
    if (
      window.confirm(
        "Reset user state? This will clear backend onboarding status, terms acceptance, and local data.",
      )
    ) {
      try {
        // Get token for backend request
        const token = await getAccessTokenSilently();
        setApiToken(token);

        // Reset backend user data
        console.log("Resetting backend user data...");
        await api.post("/api/v1/auth/reset-user-data");

        // Clear localStorage
        localStorage.removeItem("habitrack_visited");
        localStorage.removeItem("habit_logs");
        localStorage.removeItem("user_endorsements");

        console.log(
          "User state reset completed (backend + localStorage cleared)",
        );
        window.location.reload();
      } catch (error) {
        console.error("Error resetting user state:", error);
        alert("Error resetting user state. Check console for details.");
      }
    }
  };

  return (
    <div className="header">
      <div className="header-content">
        <div className="header-left">
          <h1 className="header-title">HabiTrack</h1>
          <span className="header-subtitle">
            {programmeName || "Leadership Excellence Programme"}
          </span>
        </div>
        <div className="header-right">
          <span className="user-name">
            Welcome, {user?.name || user?.email}
          </span>
          <button
            className="btn btn-secondary btn-small"
            onClick={debugUserStatus}
            title="Debug user status"
          >
            Debug
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={resetUserState}
            title="Reset user state"
          >
            Reset
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

// Auth0 Callback Handler Component
const CallbackHandler = () => {
  const { isLoading, error } = useAuth0();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error</h2>
        <p>{error.message}</p>
        <button onClick={() => (window.location.href = "/")}>
          Return Home
        </button>
      </div>
    );
  }

  // If no error and not loading, redirect to main app
  return <Navigate to="/" replace />;
};

// API Service
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Utility function to safely set Bearer token, avoiding double prefixing
const setApiToken = (token) => {
  // Remove any existing "Bearer " prefix to avoid duplication
  const cleanToken = token.replace(/^Bearer\s+/i, "");
  api.defaults.headers.Authorization = `Bearer ${cleanToken}`;
};

// Loading Component
const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

// Login Component
const LoginPage = () => {
  const { loginWithRedirect, isLoading } = useAuth0();

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>HabiTrack</h1>
        <p>Programme Mode - Leadership Excellence</p>
        <button
          className="btn btn-primary btn-large"
          onClick={() => loginWithRedirect()}
        >
          Login to Continue
        </button>
        <div className="login-footer">
          <small>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </small>
        </div>
      </div>
    </div>
  );
};

// Programme Intro Component
const ProgrammeIntro = () => {
  const navigate = useNavigate();
  const [programmeData, setProgrammeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    loadProgrammeData();
  }, []);

  const loadProgrammeData = async () => {
    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      // Get user's current programme from API
      const response = await api.get("/api/v1/programmes/current");
      const currentProgram = response.data;
      setProgrammeData(currentProgram);
      setLoading(false);
    } catch (error) {
      console.error("Error loading programme data:", error);
      // Set fallback data
      setProgrammeData({
        name: "Leadership Excellence Programme",
        description: "A comprehensive leadership development programme",
        intro_content: null,
      });
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const programmeName =
    programmeData?.name || "Leadership Excellence Programme";
  const programmeDescription =
    programmeData?.description ||
    "A comprehensive leadership development programme";

  return (
    <div className="programme-intro">
      <Header programmeName={programmeName} />
      <div className="intro-content">
        <h1>Welcome to {programmeName}</h1>

        {programmeData?.intro_content ? (
          <div
            className="intro-section"
            dangerouslySetInnerHTML={{ __html: programmeData.intro_content }}
          />
        ) : (
          <>
            <div className="intro-section">
              <h2>Programme Overview</h2>
              <p>{programmeDescription}</p>
              <p>
                This 60-day programme is designed to develop your leadership
                skills through habit-based learning. You'll work on building
                sustainable leadership behaviours that will transform how you
                interact with your team.
              </p>
            </div>

            <div className="intro-section">
              <h2>Benefits of Habit-Based Learning</h2>
              <ul>
                <li>Develop consistent leadership behaviours</li>
                <li>Track your progress with measurable outcomes</li>
                <li>Learn alongside your peers in a supportive environment</li>
                <li>Build habits that last beyond the programme</li>
              </ul>
            </div>

            <div className="intro-section">
              <h2>How It Works</h2>
              <ol>
                <li>Select a leadership behaviour to focus on</li>
                <li>Choose specific habits to practice daily</li>
                <li>Track your progress for 60 days</li>
                <li>Engage with your cohort for support and accountability</li>
                <li>
                  Complete at least 48 out of 60 days to successfully finish the
                  module
                </li>
              </ol>
            </div>
          </>
        )}

        <div className="intro-footer">
          <button
            className="btn btn-primary btn-large"
            onClick={() => navigate("/programme/behaviour-habit-selection")}
          >
            Continue to Module 1
          </button>
        </div>
      </div>
    </div>
  );
};

// Behaviour Accordion Component
const BehaviourAccordion = ({
  behaviour,
  selectedHabits,
  currentlyActiveHabits,
  maxHabits,
  onHabitToggle,
  hasSelectedHabits,
  hasActiveHabits,
}) => {
  const [isExpanded, setIsExpanded] = useState(
    hasSelectedHabits || hasActiveHabits,
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const selectedHabitsInBehaviour = behaviour.habits.filter((habit) =>
    selectedHabits.find((h) => h.id === habit.id),
  ).length;

  const activeHabitsInBehaviour = behaviour.habits.filter((habit) =>
    currentlyActiveHabits.find((ah) => ah.habit.id === habit.id),
  ).length;

  return (
    <div className="behaviour-accordion">
      <div
        className={`behaviour-accordion-header ${isExpanded ? "expanded" : ""}`}
        onClick={toggleExpanded}
      >
        <div className="behaviour-info">
          <h3>{behaviour.name}</h3>
          <p className="behaviour-description">{behaviour.description}</p>
          <div className="behaviour-stats">
            <span className="habit-count">
              {behaviour.habits.length} habits available
            </span>
            {selectedHabitsInBehaviour > 0 && (
              <span className="selected-count">
                • {selectedHabitsInBehaviour} selected
              </span>
            )}
            {activeHabitsInBehaviour > 0 && (
              <span className="active-count">
                • {activeHabitsInBehaviour} currently active
              </span>
            )}
          </div>
        </div>
        <div className="accordion-toggle">
          <span className={`arrow ${isExpanded ? "down" : "right"}`}>
            {isExpanded ? "▼" : "▶"}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="behaviour-accordion-content">
          <div className="habits-grid">
            {behaviour.habits.map((habit) => {
              const isSelected = selectedHabits.find((h) => h.id === habit.id);
              const isCurrentlyActive = currentlyActiveHabits.find(
                (ah) => ah.habit.id === habit.id,
              );
              const canSelect =
                !isSelected && selectedHabits.length < maxHabits;

              return (
                <div
                  key={habit.id}
                  className={`habit-card ${isSelected ? "selected" : ""} ${isCurrentlyActive ? "currently-active" : ""} ${!canSelect && !isSelected ? "disabled" : ""}`}
                  onClick={() =>
                    canSelect || isSelected
                      ? onHabitToggle(habit, behaviour.id)
                      : null
                  }
                >
                  <div className="habit-header">
                    <h4>{habit.name}</h4>
                    <div className="habit-meta">
                      <span className="difficulty">{habit.difficulty}</span>
                      {habit.estimated_time_minutes && (
                        <span className="time">
                          {habit.estimated_time_minutes} min
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="habit-description">{habit.description}</p>

                  {habit.instructions && (
                    <div className="habit-instructions">
                      <strong>How to practice:</strong>
                      <p>{habit.instructions}</p>
                    </div>
                  )}

                  <div className="habit-footer">
                    <span className="duration">
                      {habit.default_duration_days} days
                    </span>
                    {isCurrentlyActive && (
                      <span className="currently-active-indicator">
                        ✓ Currently Active
                      </span>
                    )}
                    {isSelected && !isCurrentlyActive && (
                      <span className="selected-indicator">✓ Selected</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Combined Behaviour and Habit Selection Component
const BehaviourHabitSelection = () => {
  const navigate = useNavigate();
  const [behaviours, setBehaviours] = useState([]);
  const [selectedHabits, setSelectedHabits] = useState([]);
  const [currentlyActiveHabits, setCurrentlyActiveHabits] = useState([]);
  const [currentProgram, setCurrentProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const maxHabits = 3;

  useEffect(() => {
    loadBehavioursWithHabits();
  }, []);

  const loadBehavioursWithHabits = async () => {
    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      // First, get user's currently active habits
      let currentActiveHabits = [];
      try {
        const activeHabitsResponse = await api.get("/api/v1/habits/my-active");
        currentActiveHabits = activeHabitsResponse.data || [];
        setCurrentlyActiveHabits(currentActiveHabits);
        console.log("Current active habits:", currentActiveHabits);
      } catch (error) {
        console.log("Could not fetch active habits:", error);
      }

      // Get user's current programme and its behaviours with habits
      console.log("Debug: Fetching current programme...");
      const currentProgrammeResponse = await api.get(
        "/api/v1/programmes/current",
      );
      const currentProgramData = currentProgrammeResponse.data;
      console.log("Debug: Current programme response:", currentProgramData);
      setCurrentProgram(currentProgramData);

      let behavioursData = [];

      if (currentProgramData) {
        console.log(
          `Debug: Fetching behaviours for programme ${currentProgramData.name} (ID: ${currentProgramData.id})`,
        );
        // Get behaviours with habits from the user's current programme
        const behavioursResponse = await api.get(
          `/api/v1/programmes/${currentProgramData.id}/behaviours`,
        );
        behavioursData = behavioursResponse.data;
        console.log("Debug: Behaviours response:", behavioursData);
        console.log("Debug: Total behaviours:", behavioursData.length);
        behavioursData.forEach((b, i) => {
          console.log(
            `Debug: Behaviour ${i + 1}: ${b.name} with ${b.habits.length} habits`,
          );
        });
      } else {
        console.log("Debug: No current programme found, using fallback");
        // Fallback: Get habits from habits endpoint
        const habitsResponse = await api.get("/api/v1/habits/");
        const habits = habitsResponse.data;

        // Group habits by behaviour (mock structure for now)
        behavioursData = [
          {
            id: "mock-behaviour-1",
            name: "Communication Skills",
            description: "Develop effective communication habits",
            habits: habits.map((habit) => ({
              id: habit.id,
              name: habit.name,
              description: habit.description,
              instructions: habit.instructions,
              difficulty: habit.difficulty,
              estimated_time_minutes: habit.estimated_time_minutes,
              default_duration_days: habit.default_duration_days,
              tips: habit.tips,
            })),
          },
        ];
      }

      setBehaviours(behavioursData);
      console.log(
        "Debug: Set behaviours state with",
        behavioursData.length,
        "behaviours",
      );

      // Pre-select currently active habits
      console.log("Debug: Starting pre-selection process");
      console.log(
        "Debug: currentActiveHabits.length:",
        currentActiveHabits.length,
      );
      console.log("Debug: currentActiveHabits:", currentActiveHabits);
      console.log("Debug: behavioursData for pre-selection:", behavioursData);

      if (currentActiveHabits.length > 0) {
        const preSelectedHabits = [];
        currentActiveHabits.forEach((activeHabit) => {
          console.log("Debug: Processing activeHabit:", activeHabit);
          console.log("Debug: Looking for habit ID:", activeHabit.habit?.id);

          // Find the habit in the behaviours data
          behavioursData.forEach((behaviour) => {
            console.log(
              "Debug: Checking behaviour:",
              behaviour.name,
              "with",
              behaviour.habits.length,
              "habits",
            );
            const foundHabit = behaviour.habits.find((h) => {
              console.log(
                "Debug: Comparing habit IDs:",
                h.id,
                "vs",
                activeHabit.habit?.id,
              );
              return h.id === activeHabit.habit?.id;
            });
            if (foundHabit) {
              console.log("Debug: Found matching habit:", foundHabit.name);
              preSelectedHabits.push({
                ...foundHabit,
                behaviourId: behaviour.id,
              });
            }
          });
        });
        console.log("Debug: Final pre-selected habits:", preSelectedHabits);
        setSelectedHabits(preSelectedHabits);
      } else {
        console.log("Debug: No active habits to pre-select");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading behaviours and habits:", error);
      console.error("Error details:", error.response?.data);
      console.error("Error status:", error.response?.status);
      setLoading(false);
    }
  };

  const handleHabitToggle = (habit, behaviourId) => {
    const habitWithBehaviour = { ...habit, behaviourId };

    if (selectedHabits.find((h) => h.id === habit.id)) {
      // Remove habit
      setSelectedHabits(selectedHabits.filter((h) => h.id !== habit.id));
    } else if (selectedHabits.length < maxHabits) {
      // Add habit
      setSelectedHabits([...selectedHabits, habitWithBehaviour]);
    }
  };

  const handleSubmit = async () => {
    // Filter out currently active habits - only select newly chosen ones
    const newlySelectedHabits = selectedHabits.filter((selectedHabit) => {
      return !currentlyActiveHabits.find(
        (activeHabit) => activeHabit.habit.id === selectedHabit.id,
      );
    });

    // Always allow progression - user can continue even with no new selections

    setSubmitting(true);

    try {
      // Only select newly chosen habits (skip already active ones)
      if (newlySelectedHabits.length > 0) {
        let successCount = 0;
        for (const habit of newlySelectedHabits) {
          try {
            await api.post("/api/v1/habits/select", {
              habit_id: habit.id,
            });
            successCount++;
          } catch (habitError) {
            console.log(
              `Habit ${habit.name} already selected or error occurred:`,
              habitError,
            );
            // Gracefully continue - don't block progression for duplicates
          }
        }
        console.log(
          `Successfully selected ${successCount} new habits out of ${newlySelectedHabits.length} attempted`,
        );
      } else {
        console.log(
          "No new habits to select - all selected habits are already active",
        );
      }

      // Always navigate to dashboard - don't block user progression
      navigate("/programme/dashboard?refreshUserFlow=true");
    } catch (error) {
      console.error("Error in habit selection process:", error);
      // Even if there's an error, don't block progression
      console.log("Continuing to dashboard despite errors");
      navigate("/programme/dashboard?refreshUserFlow=true");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="behaviour-habit-selection">
      <Header programmeName={currentProgram?.name} />
      <div className="selection-header">
        <h1>Select Your Habits</h1>
        <p>
          Choose up to {maxHabits} habits from any of the behaviours below to
          work on for the next 60 days
        </p>
        <p className="instruction-text">
          Click on each behaviour to expand and see available habits
        </p>
        <div className="selection-progress">
          <span className="selected-count">
            {selectedHabits.length} of {maxHabits} selected
          </span>
        </div>
      </div>

      <div className="behaviours-container">
        {behaviours.map((behaviour) => {
          const behaviourHasSelectedHabits = behaviour.habits.some((habit) =>
            selectedHabits.find((h) => h.id === habit.id),
          );
          const behaviourHasActiveHabits = behaviour.habits.some((habit) =>
            currentlyActiveHabits.find((ah) => ah.habit.id === habit.id),
          );

          return (
            <BehaviourAccordion
              key={behaviour.id}
              behaviour={behaviour}
              selectedHabits={selectedHabits}
              currentlyActiveHabits={currentlyActiveHabits}
              maxHabits={maxHabits}
              onHabitToggle={handleHabitToggle}
              hasSelectedHabits={behaviourHasSelectedHabits}
              hasActiveHabits={behaviourHasActiveHabits}
            />
          );
        })}
      </div>

      <div className="selection-actions">
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/programme/intro")}
          disabled={submitting}
        >
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting
            ? "Selecting Habits..."
            : (() => {
                const newlySelectedHabits = selectedHabits.filter(
                  (selectedHabit) => {
                    return !currentlyActiveHabits.find(
                      (activeHabit) =>
                        activeHabit.habit.id === selectedHabit.id,
                    );
                  },
                );
                const newCount = newlySelectedHabits.length;
                const totalCount = selectedHabits.length;

                if (newCount === 0 && totalCount > 0) {
                  return "Continue to Dashboard";
                } else if (newCount === 0 && totalCount === 0) {
                  return "Skip to Dashboard";
                } else if (newCount === totalCount) {
                  return `Start with ${totalCount} habit${totalCount !== 1 ? "s" : ""}`;
                } else {
                  return `Add ${newCount} new habit${newCount !== 1 ? "s" : ""} (${totalCount} total)`;
                }
              })()}
        </button>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentProgram, setCurrentProgram] = useState(null);
  const { getAccessTokenSilently, user } = useAuth0();

  useEffect(() => {
    loadDashboard();
  }, []);

  // Reload dashboard when URL changes (for refresh after habit logging)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("refresh")) {
      loadDashboard();
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [window.location.search]);

  const loadDashboard = async () => {
    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      // Load user's current programme
      try {
        const programResponse = await api.get("/api/v1/programmes/current");
        setCurrentProgram(programResponse.data);
      } catch (error) {
        console.log("Could not fetch current programme:", error);
      }

      console.log("Loading dashboard data from API...");
      const response = await api.get("/api/v1/dashboard/");
      console.log("Dashboard API response:", response.data);
      console.log("Dashboard API active_habits:", response.data.active_habits);
      console.log(
        "Dashboard API active_habits length:",
        response.data.active_habits?.length,
      );
      console.log(
        "Dashboard API can_add_more_habits:",
        response.data.can_add_more_habits,
      );
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      console.error("Dashboard API error details:", error.response?.data);

      // Mock data for demo with simulated progress
      const storedLogs = JSON.parse(localStorage.getItem("habit_logs") || "[]");
      const todayLogs = storedLogs.filter((log) => {
        const logDate = new Date(log.date).toDateString();
        const today = new Date().toDateString();
        return logDate === today;
      });

      const completedToday = todayLogs.filter((log) => log.completed).length;
      const totalLogs = storedLogs.filter((log) => log.completed).length;
      const progressPercent = Math.min(Math.round((totalLogs / 60) * 100), 100);

      console.log("Using fallback dashboard data");

      // Ensure proper data structure for fallback - match API response structure
      const fallbackData = {
        user_name: user?.name || "User",
        active_habits:
          totalLogs > 0
            ? [
                {
                  id: 1,
                  habit_name: "Practice Daily Check-ins",
                  progress_percentage: progressPercent,
                  current_streak: Math.min(totalLogs, 15), // Simulate streak
                  days_completed: totalLogs,
                  total_days: 60,
                  days_remaining: 60 - totalLogs,
                },
              ]
            : [],
        team_widget: {
          name: "Leadership Cohort Alpha",
          member_count: 5,
          my_rank: totalLogs > 10 ? 1 : totalLogs > 5 ? 2 : 3,
        },
        quick_stats: {},
        notifications: [],
        can_add_more_habits: true,
      };

      console.log("Setting fallback dashboard data:", fallbackData);
      setDashboardData(fallbackData);
      setLoading(false);
    }
  };

  const handleHabitTrack = (habitId) => {
    navigate(`/programme/habit-track/${habitId}`);
  };

  const handleViewCohort = () => {
    navigate("/programme/cohort");
  };

  if (loading) return <LoadingSpinner />;

  if (!dashboardData || !dashboardData.user_name) {
    return (
      <div className="dashboard">
        <Header programmeName={currentProgram?.name} />
        <div className="dashboard-header">
          <h1>Loading dashboard...</h1>
          <p>Please wait while we load your data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header programmeName={currentProgram?.name} />
      <div className="dashboard-header">
        <h1>Welcome back, {dashboardData.user_name}!</h1>
        <p className="programme-info">
          {currentProgram?.name || "Leadership Excellence Programme"}
        </p>
        <p className="module-info">
          {currentProgram?.modules?.[0]?.name ||
            "Module 1: Effective Communication"}
        </p>
      </div>

      <div className="dashboard-content">
        <div className="habits-section">
          <h2>Your Active Habits</h2>
          <div className="habits-grid">
            {(() => {
              console.log("Dashboard render check:");
              console.log(
                "  - dashboardData.active_habits:",
                dashboardData.active_habits,
              );
              console.log(
                "  - active_habits length:",
                dashboardData.active_habits?.length,
              );
              console.log(
                "  - Will show habits:",
                dashboardData.active_habits &&
                  dashboardData.active_habits.length > 0,
              );
              return null;
            })()}
            {dashboardData.active_habits &&
            dashboardData.active_habits.length > 0 ? (
              dashboardData.active_habits.map((habit) => (
                <div key={habit.id} className="habit-card dashboard-habit">
                  <h3>{habit.habit_name}</h3>
                  <div className="habit-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${habit.progress_percentage}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {Math.round(habit.progress_percentage)}% complete
                    </span>
                  </div>
                  <div className="habit-stats">
                    <div className="stat">
                      <span className="stat-label">Current Streak</span>
                      <span className="stat-value">
                        {habit.current_streak} days
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Completed</span>
                      <span className="stat-value">
                        {habit.days_completed}/{habit.total_days}
                      </span>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleHabitTrack(habit.id)}
                  >
                    Track Today
                  </button>
                </div>
              ))
            ) : (
              <div className="no-habits-message">
                <h3>No active habits yet</h3>
                <p>
                  You haven't selected any habits to track. Start by selecting
                  habits from the programme modules.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    navigate("/programme/behaviour-habit-selection")
                  }
                >
                  {currentProgram?.modules?.[0]?.name
                    ? `Continue to ${currentProgram.modules[0].name}`
                    : "Continue to Module 1"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="team-section">
          <h2>Your Cohort</h2>
          <div className="team-widget">
            <h3>
              {dashboardData.team_widget?.name || "Leadership Cohort Alpha"}
            </h3>
            <p>{dashboardData.team_widget?.member_count || 5} members</p>
            <p>Your rank: #{dashboardData.team_widget?.my_rank || 1}</p>
            <button className="btn btn-secondary" onClick={handleViewCohort}>
              View Team Progress
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cohort View Component
const CohortView = () => {
  const navigate = useNavigate();
  const [cohortData, setCohortData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentProgram, setCurrentProgram] = useState(null);
  const { getAccessTokenSilently, user } = useAuth0();

  useEffect(() => {
    loadCohortData();
  }, []);

  const loadCohortData = async () => {
    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      // Load user's current programme
      try {
        const programResponse = await api.get("/api/v1/programmes/current");
        setCurrentProgram(programResponse.data);
      } catch (error) {
        console.log("Could not fetch current programme:", error);
      }

      console.log("Loading cohort data from API...");
      const response = await api.get("/api/v1/social/cohort/members");
      // Transform API response to expected format
      console.log("Debug: Current user:", user);
      console.log("Debug: API response members:", response.data);

      const transformedData = {
        cohort_name: currentProgram?.name || "Leadership Excellence Programme",
        members: (response.data || []).map((member) => {
          console.log("Debug: Member user ID:", member.user.id);
          console.log("Debug: Member user email:", member.user.email);
          console.log("Debug: Current user sub:", user?.sub);
          console.log("Debug: Current user email:", user?.email);

          // Try multiple ways to determine if this is the current user
          const userIdFromSub = user?.sub?.split("|")[1];
          const isMe =
            user &&
            (member.user.id === userIdFromSub ||
              member.user.id === user.sub ||
              member.user.email === user.email);

          console.log("Debug: Is me calculation:", isMe);

          return {
            id: member.user.id,
            name: member.user.full_name,
            role: member.user.job_title || "Team Member",
            current_habit:
              member.active_habits && member.active_habits.length > 0
                ? member.active_habits[0].name
                : "No active habit",
            progress_percentage: Math.round(
              member.total_progress_percentage || 0,
            ),
            current_streak: member.current_streak || 0,
            days_completed: member.active_habits
              ? member.active_habits.reduce(
                  (sum, habit) => sum + (habit.days_completed || 0),
                  0,
                )
              : 0,
            total_endorsements: member.total_endorsements || 0,
            is_me: isMe,
          };
        }),
      };
      setCohortData(transformedData);
      setLoading(false);
    } catch (error) {
      console.error("Error loading cohort data:", error);
      // Mock data for demo with local storage integration
      const userLogs = JSON.parse(localStorage.getItem("habit_logs") || "[]");
      const userCompletedDays = userLogs.filter((log) => log.completed).length;
      const userProgress = Math.min(
        Math.round((userCompletedDays / 60) * 100),
        100,
      );

      // Get endorsement counts from localStorage (updated for date-based tracking)
      const endorsements = JSON.parse(
        localStorage.getItem("user_endorsements") || "{}",
      );
      const getEndorsementCount = (memberId) => {
        return Object.keys(endorsements).filter((key) =>
          key.includes(`_${memberId}_`),
        ).length;
      };

      setCohortData({
        cohort_name: currentProgram?.name || "Leadership Cohort Alpha",
        my_rank: userCompletedDays > 10 ? 1 : userCompletedDays > 5 ? 2 : 3,
        members: [
          {
            id: 1,
            name: "Sarah Johnson",
            email: "sarah.johnson@testcorp.com",
            role: "Leader",
            current_habit: "Practice Daily Check-ins",
            progress_percentage:
              user?.email === "sarah.johnson@testcorp.com" ? userProgress : 15,
            current_streak:
              user?.email === "sarah.johnson@testcorp.com"
                ? Math.min(userCompletedDays, 15)
                : 3,
            days_completed:
              user?.email === "sarah.johnson@testcorp.com"
                ? userCompletedDays
                : 9,
            total_endorsements: 12 + getEndorsementCount(1),
            is_me: user?.email === "sarah.johnson@testcorp.com",
          },
          {
            id: 2,
            name: "Mike Chen",
            email: "mike.chen@testcorp.com",
            role: "Standard",
            current_habit: "Active Listening in Meetings",
            progress_percentage:
              user?.email === "mike.chen@testcorp.com" ? userProgress : 8,
            current_streak:
              user?.email === "mike.chen@testcorp.com"
                ? Math.min(userCompletedDays, 15)
                : 2,
            days_completed:
              user?.email === "mike.chen@testcorp.com" ? userCompletedDays : 5,
            total_endorsements: 8 + getEndorsementCount(2),
            is_me: user?.email === "mike.chen@testcorp.com",
          },
          {
            id: 3,
            name: "Emma Williams",
            email: "emma.williams@testcorp.com",
            role: "Standard",
            current_habit: "Feedback Sessions",
            progress_percentage:
              user?.email === "emma.williams@testcorp.com" ? userProgress : 12,
            current_streak:
              user?.email === "emma.williams@testcorp.com"
                ? Math.min(userCompletedDays, 15)
                : 4,
            days_completed:
              user?.email === "emma.williams@testcorp.com"
                ? userCompletedDays
                : 7,
            total_endorsements: 15 + getEndorsementCount(3),
            is_me: user?.email === "emma.williams@testcorp.com",
          },
          {
            id: 4,
            name: "Alex Rodriguez",
            email: "alex.rodriguez@testcorp.com",
            role: "Standard",
            current_habit: "Practice Daily Check-ins",
            progress_percentage:
              user?.email === "alex.rodriguez@testcorp.com" ? userProgress : 20,
            current_streak:
              user?.email === "alex.rodriguez@testcorp.com"
                ? Math.min(userCompletedDays, 15)
                : 5,
            days_completed:
              user?.email === "alex.rodriguez@testcorp.com"
                ? userCompletedDays
                : 12,
            total_endorsements: 18 + getEndorsementCount(4),
            is_me: user?.email === "alex.rodriguez@testcorp.com",
          },
          {
            id: 5,
            name: "Jane Smith",
            email: "jane.smith@testcorp.com",
            role: "Standard",
            current_habit: "Active Listening in Meetings",
            progress_percentage:
              user?.email === "jane.smith@testcorp.com" ? userProgress : 6,
            current_streak:
              user?.email === "jane.smith@testcorp.com"
                ? Math.min(userCompletedDays, 15)
                : 1,
            days_completed:
              user?.email === "jane.smith@testcorp.com" ? userCompletedDays : 4,
            total_endorsements: 5 + getEndorsementCount(5),
            is_me: user?.email === "jane.smith@testcorp.com",
          },
        ],
      });
      setLoading(false);
    }
  };

  // Helper function to check if user was endorsed today
  const hasEndorsedToday = (memberId) => {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    const endorsements = JSON.parse(
      localStorage.getItem("user_endorsements") || "{}",
    );

    // Clean up old endorsements (more than 1 day old)
    const updatedEndorsements = {};
    Object.keys(endorsements).forEach((key) => {
      const parts = key.split("_");
      const endorsementDate = parts[parts.length - 1]; // Last part is the date
      if (endorsementDate === today) {
        updatedEndorsements[key] = endorsements[key];
      }
    });

    // Save cleaned endorsements back to localStorage
    if (
      Object.keys(updatedEndorsements).length !==
      Object.keys(endorsements).length
    ) {
      localStorage.setItem(
        "user_endorsements",
        JSON.stringify(updatedEndorsements),
      );
    }

    const endorsementKey = `${user.sub}_${memberId}_${today}`;
    return !!updatedEndorsements[endorsementKey];
  };

  const handleEndorse = async (memberId) => {
    // Check if already endorsed today
    if (hasEndorsedToday(memberId)) {
      alert("You can only endorse this user once per day");
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      await api.post("/api/v1/social/endorsements", {
        endorsed_user_id: memberId,
        message: "Great progress on your habit!",
      });

      // Store endorsement locally with date for persistence
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
      const endorsements = JSON.parse(
        localStorage.getItem("user_endorsements") || "{}",
      );
      const endorsementKey = `${user.sub}_${memberId}_${today}`;
      endorsements[endorsementKey] = true;
      localStorage.setItem("user_endorsements", JSON.stringify(endorsements));

      // Update local state immediately for better UX
      setCohortData((prevData) => ({
        ...prevData,
        members: prevData.members.map((member) =>
          member.id === memberId
            ? { ...member, total_endorsements: member.total_endorsements + 1 }
            : member,
        ),
      }));
    } catch (error) {
      console.error("Error endorsing member:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      alert(
        error.response?.data?.detail ||
          "Error endorsing member. Please try again.",
      );
    }
  };

  if (loading) return <LoadingSpinner />;

  // Sort members by progress percentage (descending)
  const sortedMembers = cohortData.members
    ? [...cohortData.members].sort(
        (a, b) => b.progress_percentage - a.progress_percentage,
      )
    : [];

  return (
    <div className="cohort-view">
      <Header programmeName={currentProgram?.name} />
      <div className="cohort-header">
        <h1>{cohortData.cohort_name || "Your Cohort"}</h1>
        <p>
          {cohortData.members ? cohortData.members.length : 0} members in your
          cohort
        </p>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/programme/dashboard")}
        >
          Back to Dashboard
        </button>
      </div>

      <div className="cohort-content">
        <div className="leaderboard-section">
          <h2>Leaderboard</h2>
          <div className="leaderboard">
            {sortedMembers.map((member, index) => (
              <div
                key={member.id}
                className={`member-card ${member.is_me ? "is-me" : ""}`}
              >
                <div className="member-rank">#{index + 1}</div>
                <div className="member-info">
                  <h3>
                    {member.name}
                    {member.is_me && <span className="me-badge">You</span>}
                  </h3>
                  <p className="member-role">{member.role}</p>
                  <p className="member-habit">{member.current_habit}</p>
                </div>
                <div className="member-stats">
                  <div
                    className="progress-circle"
                    style={{
                      "--progress": `${(member.progress_percentage / 100) * 360}deg`,
                    }}
                  >
                    <div className="progress-text">
                      {member.progress_percentage}%
                    </div>
                  </div>
                  <div className="stats-grid">
                    <div className="stat">
                      <span className="stat-value">
                        {member.current_streak}
                      </span>
                      <span className="stat-label">Streak</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {member.days_completed}
                      </span>
                      <span className="stat-label">Days</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {member.total_endorsements}
                      </span>
                      <span className="stat-label">Endorsements</span>
                    </div>
                  </div>
                </div>
                {!member.is_me && (
                  <div className="member-actions">
                    <button
                      className={`btn btn-small ${
                        member.current_habit === "No active habit" ||
                        hasEndorsedToday(member.id)
                          ? "btn-secondary"
                          : "btn-primary"
                      }`}
                      onClick={() => handleEndorse(member.id)}
                      disabled={
                        member.current_habit === "No active habit" ||
                        hasEndorsedToday(member.id)
                      }
                      title={
                        hasEndorsedToday(member.id)
                          ? "You have already endorsed this user today"
                          : member.current_habit === "No active habit"
                            ? "No active habits to endorse"
                            : "Endorse this member"
                      }
                    >
                      {hasEndorsedToday(member.id) ? "Endorsed" : "Endorse"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Habit Tracking Component
const HabitTracking = () => {
  const { habitId } = useParams();
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingLog, setHasExistingLog] = useState(false);
  const [currentProgram, setCurrentProgram] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    loadTodaysLog();
  }, []);

  const loadTodaysLog = () => {
    const existingLogs = JSON.parse(localStorage.getItem("habit_logs") || "[]");
    const todayDate = new Date().toDateString();

    const todaysLog = existingLogs.find((log) => {
      const logDate = new Date(log.date).toDateString();
      // Handle both old habit_id and new habitId field names for backward compatibility
      const logHabitId = log.habitId || log.habit_id;
      return logDate === todayDate && logHabitId === habitId;
    });

    if (todaysLog) {
      setCompleted(todaysLog.completed);
      setNotes(todaysLog.notes || "");
      setHasExistingLog(true);
    }
  };

  const handleSubmit = async (completedStatus) => {
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      setApiToken(token);

      // Try to log to backend first
      try {
        await api.post(`/api/v1/habits/${habitId}/log`, {
          log_type: completedStatus ? "completed" : "skipped",
          note: notes,
        });
        console.log("Debug: Habit logged to backend successfully");
      } catch (backendError) {
        console.error("Backend logging failed:", backendError);
        // Continue with local storage fallback
      }

      // Store log locally for immediate feedback and demo purposes
      const habitLog = {
        date: new Date().toISOString(),
        completed: completedStatus,
        notes: notes,
        habitId: habitId,
      };

      const existingLogs = JSON.parse(
        localStorage.getItem("habit_logs") || "[]",
      );
      const todayDate = new Date().toDateString();

      // Remove any existing log for today for this specific habit
      const filteredLogs = existingLogs.filter((log) => {
        const logDate = new Date(log.date).toDateString();
        // Handle both old habit_id and new habitId field names for backward compatibility
        const logHabitId = log.habitId || log.habit_id;
        return !(logDate === todayDate && logHabitId === habitId);
      });

      // Add new log
      filteredLogs.push(habitLog);
      localStorage.setItem("habit_logs", JSON.stringify(filteredLogs));

      // Force dashboard refresh by adding timestamp
      navigate("/programme/dashboard?refresh=" + Date.now());
    } catch (error) {
      console.error("Error logging habit:", error);
      // Still store locally for demo
      const habitLog = {
        date: new Date().toISOString(),
        completed: completedStatus,
        notes: notes,
        habitId: habitId,
      };

      const existingLogs = JSON.parse(
        localStorage.getItem("habit_logs") || "[]",
      );
      const todayDate = new Date().toDateString();

      const filteredLogs = existingLogs.filter((log) => {
        const logDate = new Date(log.date).toDateString();
        // Handle both old habit_id and new habitId field names for backward compatibility
        const logHabitId = log.habitId || log.habit_id;
        return !(logDate === todayDate && logHabitId === habitId);
      });

      filteredLogs.push(habitLog);
      localStorage.setItem("habit_logs", JSON.stringify(filteredLogs));

      navigate("/programme/dashboard?refresh=" + Date.now());
    }
    setSubmitting(false);
  };

  const handleSkip = () => {
    navigate("/programme/dashboard");
  };

  return (
    <div className="habit-tracking">
      <Header programmeName={currentProgram?.name} />
      <div className="tracking-header">
        <h1>Daily Check-in</h1>
        <p>
          {hasExistingLog
            ? "You've already logged today. You can update your entry below."
            : "Did you complete your habit today?"}
        </p>
        {hasExistingLog && (
          <div className="existing-log-notice">
            <span className="notice-icon">📝</span>
            <span>Updating today's entry</span>
          </div>
        )}
      </div>

      <div className="habit-info">
        <h2>Practice Daily Check-ins</h2>
        <p>Spend 10 minutes having one-on-one check-ins with team members</p>
      </div>

      <div className="tracking-form">
        <div className="completion-buttons">
          <button
            className={`btn btn-large ${completed === true ? "btn-success selected" : "btn-outline"}`}
            onClick={() => setCompleted(true)}
          >
            Yes, I completed it!
          </button>
          <button
            className={`btn btn-large ${completed === false ? "btn-danger selected" : "btn-outline"}`}
            onClick={() => setCompleted(false)}
          >
            No, I didn't do it today
          </button>
        </div>

        <div className="notes-section">
          <label>Notes (optional):</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How did it go? Any insights or challenges?"
            rows={4}
          />
        </div>

        <div className="tracking-footer">
          <button className="btn btn-secondary" onClick={handleSkip}>
            Skip for Now
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleSubmit(completed)}
            disabled={completed === null || submitting}
          >
            {submitting
              ? "Saving..."
              : hasExistingLog
                ? "Update Entry"
                : "Save & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App Component with Router
const AppContent = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout } =
    useAuth0();
  const [userProfile, setUserProfile] = useState(null);
  const [hasActiveHabits, setHasActiveHabits] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [navigationPath, setNavigationPath] = useState(null);
  const [userFlowLoaded, setUserFlowLoaded] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [checkingUserFlow, setCheckingUserFlow] = useState(false);
  const [timeoutTriggered, setTimeoutTriggered] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user && !checkingUserFlow && !userFlowLoaded) {
      checkOnboardingStatus();
    }
  }, [isAuthenticated, user, checkingUserFlow, userFlowLoaded]);

  // Separate useEffect for timeout to prevent multiple triggers
  useEffect(() => {
    if (!userFlowLoaded && !timeoutTriggered) {
      const timeoutId = setTimeout(() => {
        if (!userFlowLoaded && !timeoutTriggered) {
          console.log("Debug: Loading timeout - forcing fallback");
          setTimeoutTriggered(true);
          setHasAcceptedTerms(true);
          setHasActiveHabits(false);
          setNavigationPath("onboarding");
          setUserFlowLoaded(true);
        }
      }, 15000); // 15 second timeout

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [userFlowLoaded, timeoutTriggered]);

  const handleAcceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      const token = await getAccessTokenSilently();
      const localApi = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
      });
      // Remove any existing "Bearer " prefix to avoid duplication
      const cleanToken = token.replace(/^Bearer\s+/i, "");
      localApi.defaults.headers.Authorization = `Bearer ${cleanToken}`;

      await localApi.post("/api/v1/auth/accept-terms", {
        accept_terms: true,
        accept_privacy: true,
      });

      // Refresh user flow status after accepting terms
      setShowTermsModal(false);
      setHasAcceptedTerms(true);
      checkOnboardingStatus();
    } catch (error) {
      console.error("Error accepting terms:", error);
      alert("Error accepting terms. Please try again.");
    } finally {
      setAcceptingTerms(false);
    }
  };

  const handleDeclineTerms = () => {
    logout({ returnTo: window.location.origin });
  };

  // Watch for refresh parameter in URL to reload user flow status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("refreshUserFlow")) {
      console.log("Debug: Refreshing user flow status due to URL parameter");
      console.log("Debug: Current URL:", window.location.href);
      checkOnboardingStatus();
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
      console.log("Debug: URL cleaned, new URL:", window.location.href);
    }
  }, [window.location.search]);

  const checkOnboardingStatus = async () => {
    if (checkingUserFlow) {
      console.log("Debug: User flow check already in progress, skipping");
      return;
    }

    setCheckingUserFlow(true);
    try {
      console.log("Debug: Getting Auth0 access token...");
      const token = await getAccessTokenSilently();

      console.log("Debug: Token retrieved:", {
        token_length: token ? token.length : 0,
        token_preview: token ? token.substring(0, 50) + "..." : "NO_TOKEN",
        has_token: !!token,
      });

      if (!token) {
        console.error("Debug: No token received from Auth0!");
        throw new Error("No access token available");
      }

      setApiToken(token);
      console.log("Debug: Set Authorization header");

      console.log("Debug: Checking user flow status via backend...");
      try {
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await api.get("/api/v1/auth/user-flow-status", {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const flowStatus = response.data;

        console.log(
          "Debug: Backend flow status:",
          JSON.stringify(flowStatus, null, 2),
        );
        console.log("  - Navigation path:", flowStatus.navigation_path);
        console.log("  - Has accepted terms:", flowStatus.has_accepted_terms);
        console.log("  - Has active habits:", flowStatus.has_active_habits);

        // Set state based on simplified backend response
        setHasAcceptedTerms(flowStatus.has_accepted_terms);
        setHasActiveHabits(flowStatus.has_active_habits);
        setNavigationPath(flowStatus.navigation_path);
        setUserFlowLoaded(true);

        // Show terms modal if needed
        if (flowStatus.navigation_path === "terms") {
          setShowTermsModal(true);
        }

        console.log("Debug: Setting frontend state:");
        console.log(
          "  - navigationPath being set to:",
          flowStatus.navigation_path,
        );
        console.log("  - hasAcceptedTerms:", flowStatus.has_accepted_terms);
        console.log("  - hasActiveHabits:", flowStatus.has_active_habits);
        console.log("  - userFlowLoaded: true");

        // Get user profile for display
        const userResponse = await api.get("/api/v1/auth/me");
        setUserProfile(userResponse.data);

        console.log("Debug: User profile:", userResponse.data);
      } catch (apiError) {
        console.error("Debug: API calls failed:", apiError);
        throw apiError;
      }
    } catch (error) {
      console.error("Error checking user flow status:", error);
      console.log("Debug: API call failed, response:", error.response?.data);

      // Check if it's a timeout error
      if (error.name === "AbortError") {
        console.log("Debug: Request timed out - using fallback");
      } else if (error.code === "ECONNABORTED") {
        console.log("Debug: Connection timeout - using fallback");
      } else {
        console.log("Debug: Auth0 token error details:", {
          error_name: error.name,
          error_message: error.message,
          response_status: error.response?.status,
          response_headers: error.response?.headers,
          auth_header_sent: api.defaults.headers.Authorization ? "YES" : "NO",
        });
      }

      // Enhanced fallback: Check localStorage and provide reasonable defaults
      const hasVisited = localStorage.getItem("habitrack_visited");
      const hasLogs = localStorage.getItem("habit_logs");
      const hasActivity =
        hasVisited === "true" ||
        (hasLogs && JSON.parse(hasLogs || "[]").length > 0);

      console.log(
        "Debug: Using enhanced fallback - has activity:",
        hasActivity,
      );

      // Set reasonable defaults to prevent hanging
      setHasAcceptedTerms(true); // Assume terms accepted if we got this far
      setHasActiveHabits(hasActivity);
      setNavigationPath(hasActivity ? "dashboard" : "onboarding");
      setUserFlowLoaded(true);

      // Set basic user profile
      setUserProfile({
        name: user?.name || "User",
        email: user?.email || "user@example.com",
      });
    } finally {
      setCheckingUserFlow(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <>
      <TermsModal
        isOpen={showTermsModal}
        onAccept={handleAcceptTerms}
        onLogout={handleDeclineTerms}
        loading={acceptingTerms}
      />
      <Routes>
        <Route path="/callback" element={<CallbackHandler />} />
        <Route
          path="/login"
          element={<Navigate to="/programme/intro" replace />}
        />
        <Route path="/programme/intro" element={<ProgrammeIntro />} />
        <Route
          path="/programme/behaviour-selection"
          element={
            <Navigate to="/programme/behaviour-habit-selection" replace />
          }
        />
        <Route
          path="/programme/behaviour-habit-selection"
          element={<BehaviourHabitSelection />}
        />
        <Route
          path="/programme/habit-selection"
          element={
            <Navigate to="/programme/behaviour-habit-selection" replace />
          }
        />
        <Route path="/programme/dashboard" element={<Dashboard />} />
        <Route path="/programme/cohort" element={<CohortView />} />
        <Route
          path="/programme/habit-track/:habitId"
          element={<HabitTracking />}
        />
        <Route
          path="/"
          element={(() => {
            console.log("Debug: Root route decision:");
            console.log("  - userFlowLoaded:", userFlowLoaded);
            console.log("  - navigationPath:", navigationPath);
            console.log("  - hasAcceptedTerms:", hasAcceptedTerms);
            console.log("  - hasActiveHabits:", hasActiveHabits);

            // Wait for user flow data to load before making navigation decisions
            if (!userFlowLoaded) {
              console.log(
                "  - Decision: Waiting for user flow data to load...",
              );

              return (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <h2>Loading...</h2>
                  <p>Checking your account status...</p>
                  <p
                    style={{
                      fontSize: "0.9em",
                      color: "#666",
                      marginTop: "10px",
                    }}
                  >
                    This should only take a few seconds...
                  </p>
                </div>
              );
            }

            // Handle simplified navigation paths
            if (navigationPath === "terms") {
              console.log("  - Decision: Terms must be accepted first");
              // Terms modal will be shown, stay on current page
              return <div className="loading-screen">Loading...</div>;
            } else if (navigationPath === "onboarding") {
              console.log("  - Decision: Starting onboarding flow");
              return <Navigate to="/programme/intro" replace />;
            } else if (navigationPath === "dashboard") {
              console.log("  - Decision: Going to dashboard");
              return <Navigate to="/programme/dashboard" replace />;
            }

            // Fallback logic
            if (hasActiveHabits) {
              console.log("  - Decision: Going to dashboard (fallback)");
              return <Navigate to="/programme/dashboard" replace />;
            } else {
              console.log(
                "  - Decision: Going to programme intro (fallback logic)",
              );
              return <Navigate to="/programme/intro" replace />;
            }
          })()}
        />
      </Routes>
    </>
  );
};

// Root App Component
function App() {
  return (
    <Router>
      <div className="app">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;
